import { Children, Fragment, type ReactNode } from "react";
import { cn } from "./utils";

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("rounded-card border border-line bg-surface", className)}>{children}</div>;
}

// Small uppercase label above a block, such as "About" or "Catch up".
export function Eyebrow({
  children,
  tone = "meta",
  className,
}: {
  children: ReactNode;
  tone?: "meta" | "secondary" | "queravel" | "blue";
  className?: string;
}) {
  const tones = {
    meta: "text-ink-meta",
    secondary: "text-ink-secondary",
    queravel: "text-queravel",
    blue: "text-blue",
  };
  return (
    <div className={cn("text-xs font-bold tracking-[0.04em] uppercase", tones[tone], className)}>
      {children}
    </div>
  );
}

// A white card of rows separated by inset hairlines, as in settings.
export function SettingsGroup({ children, className }: { children: ReactNode; className?: string }) {
  const rows = Children.toArray(children);
  return (
    <div className={cn("overflow-hidden rounded-card border border-line bg-surface", className)}>
      {rows.map((row, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: rows keep their order
        <Fragment key={i}>
          {i > 0 && <div className="ml-4 h-px bg-line" />}
          {row}
        </Fragment>
      ))}
    </div>
  );
}

export function SettingRow({
  title,
  detail,
  trailing,
  className,
}: {
  title: ReactNode;
  detail?: ReactNode;
  trailing?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center gap-3 px-4 py-[13px]", className)}>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-[15px] font-semibold">{title}</span>
        {detail && <span className="text-[12.5px] text-ink-secondary">{detail}</span>}
      </span>
      {trailing}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  children,
  action,
  className,
}: {
  icon?: ReactNode;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col items-center justify-center gap-3 px-6 py-12 text-center", className)}>
      {icon && (
        <div className="mb-1 flex h-16 w-16 items-center justify-center rounded-[20px] bg-surface-header text-ink-secondary">
          {icon}
        </div>
      )}
      <h2 className="m-0 font-serif text-[26px] font-medium tracking-[-0.01em]">{title}</h2>
      {children && <p className="m-0 max-w-sm text-[15px] leading-normal text-ink-secondary">{children}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}
